import {computed, defineComponent, type PropType, ref, watch} from 'vue';
import {PagedResult} from "@/app/shared/helpers/paged-result.ts";
import {DocumentType, PaymentMethod, PurchaseInvoice} from "@/api/enterprise-gateway-api.proxy.ts";
import {useFormatting} from "@/composables/currency-format.ts";
import {type PurchaseInvoicesResponse, useInvoicingStore} from "@/app/stores/invoicing-store.ts";
import AegPaginator from '@/app/shared/components/aeg-paginator/aeg-paginator.vue';
import {Button, Column, DataTable} from 'primevue';
import AegDatatable from '@/app/shared/components/aeg-datatable/aeg-datatable.vue';

export default defineComponent({
    name: 'AegAdminPurchaseInvoices',
    components: {AegDatatable, DataTable, Column, Button, AegPaginator},
    props: {
        purchaseInvoices: {
            type: Object as PropType<PurchaseInvoicesResponse>,
            required: true
        },
        isLoading: {
            type: Boolean,
            required: true,
            default: false
        },
        hideHeaderPagination: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    emits: ['change-page'],
    setup: function (props, {emit}) {
        const {formatCurrency, formatDate} = useFormatting();
        const invoiceStore = useInvoicingStore();
        const pagedInvoices = ref<PagedResult<PurchaseInvoice>>(PagedResult.createEmpty<PurchaseInvoice>());
        const loading = ref<boolean>(false);
        const emptyState = ref<boolean>(false);
        const downloadFileUriLoading = ref<boolean>(false);
        const downloadFileUri = ref<string | undefined>('');

        const changePage = async (pageNumber: number) => {
            emit('change-page', pageNumber);
        }

        const showDownloadLink = computed((): boolean => {
            return !!pagedInvoices.value && pagedInvoices.value.items.length > 0;
        });

        const purchaseInvoicesChanged = (newPurchaseInvoices: PurchaseInvoicesResponse) => {
            if(!newPurchaseInvoices) return;
            pagedInvoices.value = newPurchaseInvoices.pagedResult;
            emptyState.value = newPurchaseInvoices.pagedResult.items.length === 0;
            downloadFileUri.value = newPurchaseInvoices.downloadUrl
        }

        const getDocumentTypeLabel = (documentType?: DocumentType): string => {
            return invoiceStore.getDocumentTypeLabel(documentType);
        };

        const getInvoiceTypeIcon = (documentType?: DocumentType, paymentMethod?: PaymentMethod): string => {
            return invoiceStore.getInvoiceTypeIcon(documentType, paymentMethod);
        };

        const isTopUpType = (documentType?: DocumentType): boolean => {
            return invoiceStore.isTopUpType(documentType);
        };

        watch(() => props.purchaseInvoices, purchaseInvoicesChanged, {immediate: true, deep: true});
        watch(() => props.isLoading, (newValue) => { loading.value = newValue }, {immediate: true, deep: true});

        return {
            loading,
            emptyState,
            showDownloadLink,
            pagedInvoices,
            downloadFileUri,
            downloadFileUriLoading,
            getDocumentTypeLabel,
            getInvoiceTypeIcon,
            isTopUpType,
            formatCurrency,
            formatDate,
            changePage
        };
    }
})
